import { useEffect, useRef, useState } from 'react';
import {
    BodySmallEmphasized,
    BodySmallRegular,
    FruitfulLogoIcon,
    Icon,
    IconButton,
    PanelLeftIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    buildMoneyMapperPrimaryNavRows,
    type MoneyMapperPrimaryNavRow,
    type MoneyMapperPrimarySection,
} from './moneyMapperNavigation';

const NAV_EXPANDED_WIDTH = 280;
const NAV_COLLAPSED_WIDTH = 64;
const NAV_PADDING_HORIZONTAL = 12;

type NavIconColor = '$green900' | '$gray1100' | '$gray1200';

const renderNavIcon = (
    value: MoneyMapperPrimarySection,
    RowIcon: MoneyMapperPrimaryNavRow['icon'],
    size: number,
    color: NavIconColor,
) => {
    if (value === 'accounts') {
        return (
            <YStack
                borderWidth={1}
                borderColor="$gray300"
                borderRadius={6}
                alignItems="center"
                justifyContent="center"
                width={size}
                height={size}
                flexShrink={0}
            >
                <Icon icon={FruitfulLogoIcon} size={size - 4} color={color} />
            </YStack>
        );
    }
    // Fixed-size wrapper ensures every icon occupies exactly `size` × `size` pixels
    // so text labels stay horizontally aligned across all items.
    return (
        <XStack
            width={size}
            height={size}
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
        >
            <Icon icon={RowIcon} size={size} color={color} />
        </XStack>
    );
};

interface NavRowItemProps {
    value: MoneyMapperPrimarySection;
    label: string;
    icon: MoneyMapperPrimaryNavRow['icon'];
    isSelected: boolean;
    layout: 'expanded' | 'collapsed';
    /** Controls label slide animation in the expanded layout. */
    isExpanded?: boolean;
    onPress: () => void;
}

const NavRowItem = ({
    value,
    label,
    icon,
    isSelected,
    layout,
    isExpanded = true,
    onPress,
}: NavRowItemProps) => {
    const iconColor: NavIconColor = isSelected ? '$green900' : '$gray1100';

    if (layout === 'expanded') {
        return (
            <XStack
                onPress={onPress}
                accessibilityRole="tab"
                accessibilityState={{ selected: isSelected }}
                accessibilityLabel={label}
                alignItems="center"
                justifyContent="flex-start"
                gap={10}
                paddingVertical={10}
                paddingHorizontal={10}
                borderRadius={12}
                width="100%"
                maxWidth="100%"
                cursor="pointer"
                backgroundColor={isSelected ? '$gray100' : 'transparent'}
                hoverStyle={{ backgroundColor: '$gray100' }}
            >
                {renderNavIcon(value, icon, 20, iconColor)}
                <View
                    animation="slideScreen"
                    animateOnly={['opacity', 'maxWidth']}
                    opacity={isExpanded ? 1 : 0}
                    maxWidth={isExpanded ? 200 : 0}
                    overflow="hidden"
                >
                    {isSelected ? (
                        <BodySmallEmphasized color="$green900">
                            {label}
                        </BodySmallEmphasized>
                    ) : (
                        <BodySmallRegular color="$gray1100">
                            {label}
                        </BodySmallRegular>
                    )}
                </View>
            </XStack>
        );
    }

    return (
        <XStack
            onPress={onPress}
            accessibilityRole="tab"
            accessibilityState={{ selected: isSelected }}
            accessibilityLabel={label}
            alignItems="center"
            justifyContent="center"
            padding={8}
            borderRadius={8}
            width={36}
            maxWidth="100%"
            cursor="pointer"
            backgroundColor={isSelected ? '$gray100' : 'transparent'}
            hoverStyle={{ backgroundColor: '$gray100' }}
        >
            {renderNavIcon(value, icon, 20, iconColor)}
        </XStack>
    );
};

interface MoneyMapperLeftNavExpandedProps {
    primaryRows: MoneyMapperPrimaryNavRow[];
    primarySection: MoneyMapperPrimarySection;
    onPrimarySectionChange: (section: MoneyMapperPrimarySection) => void;
    onToggleExpanded: () => void;
    calculator: React.ReactNode;
    simulator?: React.ReactNode;
    mapFooter: React.ReactNode;
    isExpanded: boolean;
}

const MoneyMapperLeftNavExpanded = ({
    primaryRows,
    primarySection,
    onPrimarySectionChange,
    onToggleExpanded,
    calculator,
    simulator,
    mapFooter,
    isExpanded,
}: MoneyMapperLeftNavExpandedProps) => {
    return (
        <YStack
            width="100%"
            flex={1}
            minHeight={0}
            paddingTop={16}
            paddingBottom={16}
            paddingHorizontal={NAV_PADDING_HORIZONTAL}
            gap={8}
        >
            <XStack
                alignItems="center"
                justifyContent="space-between"
                paddingHorizontal={4}
                marginBottom={8}
            >
                <Icon icon={FruitfulLogoIcon} size={32} color="$green900" />
                <IconButton
                    variant="tertiary"
                    size="tiny"
                    icon={PanelLeftIcon}
                    onPress={onToggleExpanded}
                />
            </XStack>
            <YStack
                flex={1}
                marginTop={8}
                gap={8}
                minHeight={0}
                alignItems="stretch"
            >
                {primaryRows.map(({ value, label, icon }) => (
                    <NavRowItem
                        key={value}
                        value={value}
                        label={label}
                        icon={icon}
                        isSelected={primarySection === value}
                        layout="expanded"
                        isExpanded={isExpanded}
                        onPress={() => {
                            onPrimarySectionChange(value);
                        }}
                    />
                ))}
            </YStack>
            <YStack gap={12} flexShrink={0} width="100%">
                {calculator != null && (
                    <YStack width="100%" minWidth={248} alignItems="stretch">
                        {calculator}
                    </YStack>
                )}
                {simulator != null && (
                    <YStack width="100%" alignItems="stretch">
                        {simulator}
                    </YStack>
                )}
                <YStack
                    borderTopWidth={1}
                    borderTopColor="$gray200"
                    marginHorizontal={-NAV_PADDING_HORIZONTAL}
                    paddingHorizontal={NAV_PADDING_HORIZONTAL}
                    paddingTop={12}
                    gap={10}
                    width="auto"
                >
                    {mapFooter}
                </YStack>
            </YStack>
        </YStack>
    );
};

interface MoneyMapperLeftNavCollapsedProps {
    primaryRows: MoneyMapperPrimaryNavRow[];
    primarySection: MoneyMapperPrimarySection;
    onPrimarySectionChange: (section: MoneyMapperPrimarySection) => void;
    onToggleExpanded: () => void;
    calculator: React.ReactNode;
    simulator?: React.ReactNode;
    mapFooter: React.ReactNode;
}

const MoneyMapperLeftNavCollapsed = ({
    primaryRows,
    primarySection,
    onPrimarySectionChange,
    onToggleExpanded,
    calculator,
    simulator,
    mapFooter,
}: MoneyMapperLeftNavCollapsedProps) => {
    const [isNavHovered, setIsNavHovered] = useState(false);
    const containerRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const el = containerRef.current;
        if (el == null) return;
        const onEnter = () => {
            setIsNavHovered(true);
        };
        const onLeave = () => {
            setIsNavHovered(false);
        };
        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('mouseleave', onLeave);
        return () => {
            el.removeEventListener('mouseenter', onEnter);
            el.removeEventListener('mouseleave', onLeave);
        };
    }, []);

    return (
        <YStack
            ref={(el) => {
                containerRef.current = el as HTMLElement | null;
            }}
            width="100%"
            flex={1}
            minHeight={0}
            paddingTop={8}
            paddingBottom={12}
            paddingHorizontal={6}
            gap={6}
        >
            <YStack alignItems="center" marginBottom={4} width="100%">
                <XStack
                    alignItems="center"
                    justifyContent="center"
                    width={40}
                    height={40}
                    borderRadius={8}
                    cursor="pointer"
                    hoverStyle={{ backgroundColor: '$gray50' }}
                    onPress={onToggleExpanded}
                >
                    <Icon
                        icon={isNavHovered ? PanelLeftIcon : FruitfulLogoIcon}
                        size={22}
                        color="$green900"
                    />
                </XStack>
            </YStack>
            <YStack
                flex={1}
                marginTop={8}
                gap={8}
                minHeight={0}
                alignItems="center"
            >
                {primaryRows.map(({ value, label, icon }) => (
                    <NavRowItem
                        key={value}
                        value={value}
                        label={label}
                        icon={icon}
                        isSelected={primarySection === value}
                        layout="collapsed"
                        onPress={() => {
                            onPrimarySectionChange(value);
                        }}
                    />
                ))}
            </YStack>
            <YStack gap={8} flexShrink={0} width="100%">
                {calculator != null && (
                    <YStack width="100%" alignItems="center">
                        {calculator}
                    </YStack>
                )}
                {simulator != null && (
                    <YStack width="100%" alignItems="center">
                        {simulator}
                    </YStack>
                )}
                <YStack
                    borderTopWidth={1}
                    borderTopColor="$gray200"
                    paddingTop={8}
                    gap={8}
                    width="100%"
                >
                    {mapFooter}
                </YStack>
            </YStack>
        </YStack>
    );
};

interface MoneyMapperLeftNavProps {
    isExpanded: boolean;
    onToggleExpanded: () => void;
    primarySection: MoneyMapperPrimarySection;
    onPrimarySectionChange: (section: MoneyMapperPrimarySection) => void;
    calculator: React.ReactNode;
    simulator?: React.ReactNode;
    mapFooter: React.ReactNode;
}

export const MoneyMapperLeftNav = ({
    isExpanded,
    onToggleExpanded,
    primarySection,
    onPrimarySectionChange,
    calculator,
    simulator,
    mapFooter,
}: MoneyMapperLeftNavProps) => {
    const primaryRows = buildMoneyMapperPrimaryNavRows();

    const navBody = isExpanded ? (
        <MoneyMapperLeftNavExpanded
            primaryRows={primaryRows}
            primarySection={primarySection}
            onPrimarySectionChange={onPrimarySectionChange}
            onToggleExpanded={onToggleExpanded}
            calculator={calculator}
            simulator={simulator}
            mapFooter={mapFooter}
            isExpanded={isExpanded}
        />
    ) : (
        <MoneyMapperLeftNavCollapsed
            primaryRows={primaryRows}
            primarySection={primarySection}
            onPrimarySectionChange={onPrimarySectionChange}
            onToggleExpanded={onToggleExpanded}
            calculator={calculator}
            simulator={simulator}
            mapFooter={mapFooter}
        />
    );

    return (
        <View
            animation="slideScreen"
            animateOnly={['width']}
            width={isExpanded ? NAV_EXPANDED_WIDTH : NAV_COLLAPSED_WIDTH}
            flexShrink={0}
            alignSelf="stretch"
            overflow="hidden"
            borderRightWidth={1}
            borderRightColor="rgba(0,0,0,0.06)"
            backgroundColor="$white"
        >
            {navBody}
        </View>
    );
};
